import L from "leaflet";
import { vectorBasemapLayer } from "esri-leaflet-vector";
import "./styles.css";
import { formatDdm, formatUtm } from "./coordinates";
import {
  createEmergencyLink,
  fetchCustomLocations,
  fetchEmergencyLinkState,
  fetchLocations,
  fetchLocationsForSession,
  fetchSessions,
  fetchWeather,
  fetchStatus,
  isMockMode,
  revokeEmergencyLink
} from "./api";
import { buildEmergencySummary, buildShareText, mapsUrl } from "./emergency";
import {
  ageLabel,
  buildGpx,
  buildStatusBanner,
  durationBetween,
  isContactStale,
  shouldShowHeadingIndicator,
  splitTrackByGap,
  uploadDelayLabel
} from "./track";
import type {
  EmergencyLinkState,
  LocationPoint,
  LocationsResponse,
  SessionsResponse,
  StatusResponse,
  TrackingSession,
  WeatherResponse
} from "./types";

type BasemapMode = "map" | "satellite" | "satellite-labels" | "topo";

const map = L.map("map", {
  zoomControl: true
}).setView([49.2827, -123.1207], 13);

const arcgisApiKey = import.meta.env.VITE_ARCGIS_API_KEY?.trim();
let activeBasemap: L.Layer | null = null;
const mapBasemap = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
  maxZoom: 19,
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
});

let latestMarker: L.Marker | null = null;
let accuracyCircle: L.Circle | null = null;
let headingCone: L.Polygon | null = null;
const routeLayers: L.LayerGroup = L.layerGroup().addTo(map);
let latestLocation: LocationPoint | null = null;
let currentRange = "4h";
let selectedSessionId = "";
let currentLocations: LocationPoint[] = [];
let currentSessions: TrackingSession[] = [];
let statusRequestInFlight = false;
let trackRequestInFlight = false;
let initialCentered = false;
let currentStatus: StatusResponse | null = null;
let emergencyLinkUrl: string | null = loadStoredEmergencyLink();
const emergencyLinkStorageKey = "find-neil-emergency-link";

const elements = {
  mockBanner: byId("mock-banner"),
  statusBanner: byId("status-banner"),
  latestRecorded: byId("latest-recorded"),
  latestUploaded: byId("latest-uploaded"),
  latestContact: byId("latest-contact"),
  ageIndicator: byId("age-indicator"),
  battery: byId("battery"),
  trackingActive: byId("tracking-active"),
  weatherTemperature: byId("weather-temperature"),
  weatherPrecipitation: byId("weather-precipitation"),
  weatherPrecipitationProbability: byId("weather-precipitation-probability"),
  weatherWind: byId("weather-wind"),
  lastRefresh: byId("last-refresh"),
  staleWarning: byId("stale-warning"),
  emergencyDetails: byId("emergency-details") as HTMLButtonElement,
  emergencyDialog: byId("emergency-dialog") as HTMLDialogElement,
  emergencySummary: byId("emergency-summary"),
  copyEmergency: byId("copy-emergency") as HTMLButtonElement,
  shareEmergency: byId("share-emergency") as HTMLButtonElement,
  openMaps: byId("open-maps") as HTMLAnchorElement,
  createEmergencyLink: byId("create-emergency-link") as HTMLButtonElement,
  copyEmergencyLink: byId("copy-emergency-link") as HTMLButtonElement,
  shareEmergencyLink: byId("share-emergency-link") as HTMLButtonElement,
  revokeEmergencyLink: byId("revoke-emergency-link") as HTMLButtonElement,
  emergencyLinkState: byId("emergency-link-state"),
  emergencyRecorded: byId("emergency-recorded"),
  emergencyAge: byId("emergency-age"),
  emergencyAccuracy: byId("emergency-accuracy"),
  emergencyDecimalDegrees: byId("emergency-decimal-degrees"),
  emergencyDdm: byId("emergency-ddm"),
  emergencyUtm: byId("emergency-utm"),
  emergencyAltitude: byId("emergency-altitude"),
  emergencySpeed: byId("emergency-speed"),
  emergencyBearing: byId("emergency-bearing"),
  emergencyBattery: byId("emergency-battery"),
  emergencyCharging: byId("emergency-charging"),
  emergencyTracking: byId("emergency-tracking"),
  emergencyMessage: byId("emergency-message"),
  emergencyFeedback: byId("emergency-feedback"),
  centerLatest: byId("center-latest") as HTMLButtonElement,
  manualRefresh: byId("manual-refresh") as HTMLButtonElement,
  exportGpx: byId("export-gpx") as HTMLButtonElement,
  basemapSelect: byId("basemap-select") as unknown as HTMLSelectElement,
  basemapMessage: byId("basemap-message"),
  sessionSelect: byId("session-select") as unknown as HTMLSelectElement,
  sessionSummary: byId("session-summary"),
  customRange: byId("custom-range") as HTMLFormElement,
  customStart: byId("custom-start") as HTMLInputElement,
  customEnd: byId("custom-end") as HTMLInputElement
};

if (isMockMode()) {
  elements.mockBanner.hidden = false;
}

configureBasemapSelector();
setBasemap(defaultBasemap());
wireEvents();
void refreshAll();
startPolling();

function wireEvents() {
  elements.centerLatest.addEventListener("click", () => {
    if (latestLocation) {
      map.setView([latestLocation.latitude, latestLocation.longitude], 16);
    }
  });

  elements.manualRefresh.addEventListener("click", () => {
    void refreshAll();
  });

  elements.exportGpx.addEventListener("click", exportCurrentGpx);
  elements.emergencyDetails.addEventListener("click", () => {
    void openEmergencyDetails();
  });
  elements.copyEmergency.addEventListener("click", () => {
    void copyEmergencySummary();
  });
  elements.shareEmergency.addEventListener("click", () => {
    void shareEmergencySummary();
  });
  elements.createEmergencyLink.addEventListener("click", () => {
    void createEmergencyLinkFromDialog();
  });
  elements.copyEmergencyLink.addEventListener("click", () => {
    void copyEmergencyLink();
  });
  elements.shareEmergencyLink.addEventListener("click", () => {
    void shareEmergencyLink();
  });
  elements.revokeEmergencyLink.addEventListener("click", () => {
    void revokeEmergencyLinkFromDialog();
  });

  elements.sessionSelect.addEventListener("change", () => {
    selectedSessionId = elements.sessionSelect.value;
    renderSessionSummary();
    void refreshTrack();
  });

  document.querySelectorAll<HTMLButtonElement>("[data-range]").forEach((button) => {
    button.addEventListener("click", async () => {
      const range = button.dataset.range;
      if (!range) return;
      currentRange = range;
      selectedSessionId = "";
      setActiveRange(range);
      await refreshSessionsAndTrack();
    });
  });

  elements.customRange.addEventListener("submit", async (event) => {
    event.preventDefault();
    const start = localDateTimeToIso(elements.customStart.value);
    const end = localDateTimeToIso(elements.customEnd.value);
    if (!start || !end) return;

    clearActiveRanges();
    selectedSessionId = "";
    const locations = await fetchCustomLocations(start, end);
    renderTrack(locations);
    markRefreshSuccess();
  });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void refreshAll();
  });
}

function startPolling() {
  window.setInterval(() => {
    if (!document.hidden) void refreshStatus();
  }, 30_000);
  window.setInterval(() => {
    if (!document.hidden) void refreshTrack();
  }, 60_000);
}

async function refreshAll() {
  await Promise.all([refreshStatus(), refreshSessionsAndTrack()]);
}

async function refreshStatus() {
  if (statusRequestInFlight) return;
  statusRequestInFlight = true;
  try {
    const status = await fetchStatus();
    renderStatus(status);
    await refreshWeather(status);
    markRefreshSuccess();
  } finally {
    statusRequestInFlight = false;
  }
}

async function refreshSessionsAndTrack() {
  const [sessions] = await Promise.all([
    fetchSessions(currentRange),
    refreshTrack()
  ]);
  renderSessions(sessions);
}

async function refreshTrack() {
  if (trackRequestInFlight) return;
  trackRequestInFlight = true;
  try {
    const locations = selectedSessionId
      ? await fetchLocationsForSession(currentRange, selectedSessionId)
      : await fetchLocations(currentRange);
    renderTrack(locations);
    markRefreshSuccess();
  } finally {
    trackRequestInFlight = false;
  }
}

function renderStatus(status: StatusResponse) {
  currentStatus = status;
  latestLocation = status.latestLocation;
  elements.statusBanner.textContent = buildStatusBanner(status);
  elements.statusBanner.className = `status-banner ${statusBannerClass(status)}`;
  elements.latestRecorded.textContent = formatTimestamp(status.latestRecordedAt);
  elements.latestUploaded.textContent = formatTimestamp(status.latestServerReceivedAt);
  elements.latestContact.textContent = formatTimestamp(status.latestHeartbeatAt);
  elements.ageIndicator.textContent = ageLabel(status.latestHeartbeatAt);
  elements.battery.textContent =
    status.batteryPercentage == null ? "-" : `${status.batteryPercentage}%`;
  elements.trackingActive.textContent =
    status.trackingActive == null ? "-" : status.trackingActive ? "Yes" : "No";
  elements.staleWarning.hidden = !isContactStale(status.latestHeartbeatAt);

  if (status.latestLocation) {
    renderLatest(status.latestLocation, !initialCentered);
    initialCentered = true;
  }
  renderHeadingIndicator(status);
}

async function openEmergencyDetails() {
  if (!currentStatus) await refreshStatus();
  if (!currentStatus) return;
  renderEmergencyDialog(currentStatus);
  elements.emergencyDialog.showModal();
  try {
    renderEmergencyLinkState(await fetchEmergencyLinkState());
  } catch {
    setEmergencyFeedback("Emergency link state is unavailable.");
  }
}

function renderEmergencyDialog(status: StatusResponse) {
  const summary = buildEmergencySummary(status);
  elements.emergencySummary.textContent = summary;
  if (status.latestLocation) {
    elements.openMaps.href = mapsUrl(status.latestLocation.latitude, status.latestLocation.longitude);
    elements.openMaps.removeAttribute("aria-disabled");
  } else {
    elements.openMaps.href = "#";
    elements.openMaps.setAttribute("aria-disabled", "true");
  }
  elements.emergencyRecorded.textContent = formatTimestamp(status.latestRecordedAt);
  elements.emergencyAge.textContent = status.latestLocation ? ageLabel(status.latestLocation.recordedAt) : "-";
  elements.emergencyAccuracy.textContent = status.latestLocation
    ? `±${Math.round(status.latestLocation.accuracyMeters)} m`
    : "-";
  elements.emergencyDecimalDegrees.textContent = status.latestLocation
    ? `${status.latestLocation.latitude}, ${status.latestLocation.longitude}`
    : "-";
  elements.emergencyDdm.textContent = status.latestLocation
    ? formatDdm(status.latestLocation.latitude, status.latestLocation.longitude)
    : "-";
  elements.emergencyUtm.textContent = status.latestLocation
    ? formatUtm(status.latestLocation.latitude, status.latestLocation.longitude)
    : "-";
  elements.emergencyAltitude.textContent =
    status.latestLocation?.altitudeMeters == null
      ? "-"
      : `${Math.round(status.latestLocation.altitudeMeters)} m`;
  elements.emergencySpeed.textContent =
    status.latestLocation?.speedMps == null
      ? "-"
      : `${status.latestLocation.speedMps.toFixed(1)} m/s`;
  elements.emergencyBearing.textContent =
    status.latestLocation?.bearingDegrees == null
      ? "-"
      : `${Math.round(status.latestLocation.bearingDegrees)}°`;
  elements.emergencyBattery.textContent =
    status.latestLocation?.batteryPercentage == null
      ? "-"
      : `${status.latestLocation.batteryPercentage}%`;
  elements.emergencyCharging.textContent =
    status.latestLocation?.isCharging == null
      ? "-"
      : status.latestLocation.isCharging ? "Yes" : "No";
  elements.emergencyTracking.textContent =
    status.trackingActive == null ? "-" : status.trackingActive ? "Active" : "Stopped";
  elements.emergencyMessage.textContent = status.quickMessage && status.quickMessageRecordedAt
    ? `${status.quickMessage} · ${formatTimestamp(status.quickMessageRecordedAt)}`
    : "-";
  setEmergencyFeedback("");
}

function renderHeadingIndicator(status: StatusResponse) {
  if (headingCone) {
    map.removeLayer(headingCone);
    headingCone = null;
  }

  if (!shouldShowHeadingIndicator(status, status.latestLocation)) return;
  const point = status.latestLocation!;
  const distanceMeters = Math.max(80, Math.min(450, point.speedMps! * 150));
  const origin: L.LatLngExpression = [point.latitude, point.longitude];
  const left = destinationPoint(point.latitude, point.longitude, point.bearingDegrees! - 18, distanceMeters);
  const center = destinationPoint(point.latitude, point.longitude, point.bearingDegrees!, distanceMeters * 1.25);
  const right = destinationPoint(point.latitude, point.longitude, point.bearingDegrees! + 18, distanceMeters);

  headingCone = L.polygon([origin, left, center, right], {
    color: "#b00020",
    weight: 1,
    fillColor: "#ef4444",
    fillOpacity: 0.22,
    opacity: 0.65
  }).addTo(map);
}

async function copyEmergencySummary() {
  const text = elements.emergencySummary.textContent ?? "";
  setEmergencyFeedback(
    await copyText(text)
      ? "Location details copied."
      : "Clipboard unavailable. Select and copy the text manually."
  );
}

async function shareEmergencySummary() {
  const text = buildShareText(
    elements.emergencySummary.textContent ?? "",
    currentStatus?.latestLocation ?? null,
    emergencyLinkUrl
  );
  if ("share" in navigator) {
    try {
      await navigator.share({ title: "Emergency location information", text });
      setEmergencyFeedback("Location details shared.");
      return;
    } catch {
      // Fall back to clipboard.
    }
  }
  setEmergencyFeedback(
    await copyText(text)
      ? "Sharing is unavailable. Details copied instead."
      : "Sharing and clipboard are unavailable."
  );
}

async function createEmergencyLinkFromDialog() {
  try {
    const state = await createEmergencyLink();
    renderEmergencyLinkState({ ...state, active: true });
    setEmergencyFeedback("Emergency link created.");
  } catch {
    setEmergencyFeedback("Emergency link could not be created.");
  }
}

async function revokeEmergencyLinkFromDialog() {
  try {
    const state = await revokeEmergencyLink();
    emergencyLinkUrl = null;
    renderEmergencyLinkState({ ...state, active: false });
    setEmergencyFeedback("Emergency link revoked.");
  } catch {
    setEmergencyFeedback("Emergency link could not be revoked.");
  }
}

async function copyEmergencyLink() {
  if (!emergencyLinkUrl) {
    setEmergencyFeedback("Create an emergency link first.");
    return;
  }
  setEmergencyFeedback(
    await copyText(emergencyLinkUrl) ? "Emergency link copied." : "Clipboard unavailable."
  );
}

async function shareEmergencyLink() {
  if (!emergencyLinkUrl) {
    setEmergencyFeedback("Create an emergency link first.");
    return;
  }
  if ("share" in navigator) {
    try {
      await navigator.share({ title: "Temporary emergency link", url: emergencyLinkUrl });
      setEmergencyFeedback("Emergency link shared.");
      return;
    } catch {
      // Fall back to clipboard.
    }
  }
  setEmergencyFeedback(
    await copyText(emergencyLinkUrl)
      ? "Sharing is unavailable. Link copied instead."
      : "Sharing unavailable."
  );
}

function renderEmergencyLinkState(state: EmergencyLinkState) {
  if (state.url) {
    emergencyLinkUrl = state.url;
    storeEmergencyLink(state.url, state.expiresAt);
  }
  if (state.active) {
    elements.emergencyLinkState.textContent =
      `Active until ${formatTimestamp(state.expiresAt)}. Shows the latest location and ${state.routeHours} hours of route history.`;
  } else if (state.revokedAt) {
    clearStoredEmergencyLink();
    elements.emergencyLinkState.textContent = `Revoked at ${formatTimestamp(state.revokedAt)}.`;
  } else {
    clearStoredEmergencyLink();
    elements.emergencyLinkState.textContent = "No active link.";
  }
}

function storeEmergencyLink(url: string, expiresAt: string | null) {
  try {
    window.localStorage.setItem(
      emergencyLinkStorageKey,
      JSON.stringify({ url, expiresAt })
    );
  } catch {
    // Link copy/share still works for this runtime session.
  }
}

function loadStoredEmergencyLink(): string | null {
  try {
    const raw = window.localStorage.getItem("find-neil-emergency-link");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { url?: unknown; expiresAt?: unknown };
    if (typeof parsed.expiresAt === "string" && Date.parse(parsed.expiresAt) <= Date.now()) {
      window.localStorage.removeItem("find-neil-emergency-link");
      return null;
    }
    return typeof parsed.url === "string" ? parsed.url : null;
  } catch {
    return null;
  }
}

function clearStoredEmergencyLink() {
  emergencyLinkUrl = null;
  try {
    window.localStorage.removeItem(emergencyLinkStorageKey);
  } catch {
    // Ignore storage failures.
  }
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function setEmergencyFeedback(message: string) {
  elements.emergencyFeedback.textContent = message;
}

async function refreshWeather(status: StatusResponse) {
  if (status.trackingActive !== true || !status.latestLocation) {
    renderWeather({
      available: false,
      temperatureCelsius: null,
      precipitationMm: null,
      precipitationProbabilityPercent: null,
      windSpeedKph: null,
      windDirectionDegrees: null,
      unavailableReason: "tracking_inactive"
    });
    return;
  }

  try {
    renderWeather(
      await fetchWeather(
        status.latestLocation.latitude,
        status.latestLocation.longitude
      )
    );
  } catch {
    renderWeather({
      available: false,
      temperatureCelsius: null,
      precipitationMm: null,
      precipitationProbabilityPercent: null,
      windSpeedKph: null,
      windDirectionDegrees: null,
      unavailableReason: "weather_unavailable"
    });
  }
}

function renderWeather(weather: WeatherResponse) {
  if (!weather.available) {
    elements.weatherTemperature.textContent = "Unavailable";
    elements.weatherPrecipitation.textContent = "-";
    elements.weatherPrecipitationProbability.textContent = "-";
    elements.weatherWind.textContent = "-";
    return;
  }

  elements.weatherTemperature.textContent =
    weather.temperatureCelsius == null ? "-" : `${Math.round(weather.temperatureCelsius)} °C`;
  elements.weatherPrecipitation.textContent =
    weather.precipitationMm == null ? "-" : `${weather.precipitationMm.toFixed(1)} mm`;
  elements.weatherPrecipitationProbability.textContent =
    weather.precipitationProbabilityPercent == null
      ? "-"
      : `${Math.round(weather.precipitationProbabilityPercent)}%`;
  elements.weatherWind.textContent =
    weather.windSpeedKph == null || weather.windDirectionDegrees == null
      ? "-"
      : `${Math.round(weather.windSpeedKph)} km/h ${windDirectionLabel(weather.windDirectionDegrees)}`;
}

function statusBannerClass(status: StatusResponse): string {
  if (status.trackingActive === false) return "status-banner-stopped";
  if (isContactStale(status.latestHeartbeatAt)) return "status-banner-stale";
  return "status-banner-active";
}

function renderSessions(response: SessionsResponse) {
  currentSessions = response.sessions;
  const existing = selectedSessionId;
  elements.sessionSelect.innerHTML = `<option value="">All sessions</option>` +
    response.sessions.map((session) => {
      const label = `${formatTimestamp(session.startedAt)} · ${session.active ? "active" : "complete"} · ${session.pointCount} points`;
      return `<option value="${escapeHtml(session.sessionId)}">${escapeHtml(label)}</option>`;
    }).join("");
  elements.sessionSelect.value = response.sessions.some((item) => item.sessionId === existing)
    ? existing
    : "";
  selectedSessionId = elements.sessionSelect.value;
  renderSessionSummary();
}

function renderSessionSummary() {
  if (!selectedSessionId) {
    elements.sessionSummary.textContent = "All sessions";
    return;
  }
  const session = currentSessions.find((item) => item.sessionId === selectedSessionId);
  if (!session) {
    elements.sessionSummary.textContent = "Session not found in this range";
    return;
  }
  elements.sessionSummary.textContent =
    `${session.active ? "Active" : "Complete"} · ${durationBetween(session.startedAt, session.endedAt)} · ${session.pointCount} points`;
}

function renderTrack(response: LocationsResponse) {
  routeLayers.clearLayers();
  currentLocations = response.locations;
  const segments = splitTrackByGap(response.locations);

  for (const segment of segments) {
    if (segment.length >= 2) {
      L.polyline(
        segment.map((point) => [point.latitude, point.longitude]),
        { color: "#1769aa", weight: 4, opacity: 0.82 }
      ).addTo(routeLayers);
    }

    for (const point of segment) {
      L.circleMarker([point.latitude, point.longitude], {
        radius: 4,
        color: "#17202a",
        weight: 1,
        fillColor: "#2d9cdb",
        fillOpacity: 0.8
      })
        .bindPopup(pointPopup(point))
        .addTo(routeLayers);
    }
  }

  if (!latestLocation && response.locations.length > 0) {
    latestLocation = response.locations[response.locations.length - 1]!;
    renderLatest(latestLocation, !initialCentered);
    initialCentered = true;
  }
}

function renderLatest(point: LocationPoint, center: boolean) {
  const latLng: L.LatLngExpression = [point.latitude, point.longitude];

  if (!latestMarker) {
    latestMarker = L.marker(latLng).addTo(map);
  } else {
    latestMarker.setLatLng(latLng);
  }

  latestMarker.bindPopup("Latest location");

  if (!accuracyCircle) {
    accuracyCircle = L.circle(latLng, {
      radius: point.accuracyMeters,
      color: "#1769aa",
      fillColor: "#1769aa",
      fillOpacity: 0.12
    }).addTo(map);
  } else {
    accuracyCircle.setLatLng(latLng);
    accuracyCircle.setRadius(point.accuracyMeters);
  }

  if (center) {
    map.setView(latLng, Math.max(map.getZoom(), 14));
  }
}

function pointPopup(point: LocationPoint): string {
  const delay = uploadDelayLabel(point);
  const rows = [
    `<strong>${escapeHtml(formatTimestamp(point.recordedAt))}</strong>`,
    `Uploaded: ${escapeHtml(formatTimestamp(point.serverReceivedAt))}`,
    delay ? `Upload delay: ${escapeHtml(delay)}` : null,
    `Accuracy: ${Math.round(point.accuracyMeters)} m`,
    point.speedMps != null && point.speedMps >= 0 ? `Speed: ${point.speedMps.toFixed(1)} m/s` : null,
    point.altitudeMeters != null ? `Altitude: ${Math.round(point.altitudeMeters)} m` : null,
    point.bearingDegrees != null ? `Bearing: ${Math.round(point.bearingDegrees)}°` : null,
    point.batteryPercentage != null ? `Battery: ${point.batteryPercentage}%` : null,
    point.isCharging != null ? `Charging: ${point.isCharging ? "yes" : "no"}` : null,
    point.sessionId ? `Session: ${escapeHtml(point.sessionId.slice(0, 8))}` : "Legacy ungrouped point"
  ].filter(Boolean);
  return rows.join("<br />");
}

function exportCurrentGpx() {
  const label = selectedSessionId
    ? `tracking-session-${selectedSessionId.slice(0, 8)}`
    : `tracking-${currentRange}-${new Date().toISOString().slice(0, 10)}`;
  const gpx = buildGpx(currentLocations, currentSessions, label);
  const blob = new Blob([gpx], { type: "application/gpx+xml" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${label}.gpx`;
  link.click();
  URL.revokeObjectURL(url);
}

function configureBasemapSelector() {
  const satelliteOptions = elements.basemapSelect.querySelectorAll<HTMLOptionElement>(
    'option[value="satellite"], option[value="satellite-labels"], option[value="topo"]'
  );

  satelliteOptions.forEach((option) => {
    option.disabled = !arcgisApiKey;
  });

  if (!arcgisApiKey) {
    elements.basemapMessage.textContent =
      "Satellite basemaps need VITE_ARCGIS_API_KEY in .env.local before production build.";
    elements.basemapMessage.hidden = false;
  }

  elements.basemapSelect.addEventListener("change", () => {
    setBasemap(elements.basemapSelect.value as BasemapMode);
  });
}

function defaultBasemap(): BasemapMode {
  return arcgisApiKey ? "satellite" : "map";
}

function setBasemap(mode: BasemapMode) {
  if (activeBasemap) {
    map.removeLayer(activeBasemap);
  }

  if (mode === "map") {
    activeBasemap = mapBasemap;
    elements.basemapMessage.hidden = Boolean(arcgisApiKey);
  } else if (!arcgisApiKey) {
    elements.basemapSelect.value = "map";
    activeBasemap = mapBasemap;
    elements.basemapMessage.textContent =
      "Satellite basemaps are unavailable because the ArcGIS API key is not configured.";
    elements.basemapMessage.hidden = false;
  } else {
    activeBasemap = createArcgisBasemap(mode);
    elements.basemapSelect.value = mode;
    elements.basemapMessage.hidden = true;
  }

  activeBasemap.addTo(map);
  if ("bringToBack" in activeBasemap && typeof activeBasemap.bringToBack === "function") {
    activeBasemap.bringToBack();
  }
}

function createArcgisBasemap(mode: Exclude<BasemapMode, "map">): L.Layer {
  const style = mode === "satellite"
    ? "arcgis/imagery"
    : mode === "satellite-labels"
      ? "arcgis/imagery/labels"
      : "arcgis/topographic";
  return vectorBasemapLayer(style, {
    token: arcgisApiKey,
    version: 2
  }) as L.Layer;
}

function setActiveRange(range: string) {
  clearActiveRanges();
  const button = document.querySelector<HTMLButtonElement>(`[data-range="${range}"]`);
  button?.classList.add("active");
}

function clearActiveRanges() {
  document.querySelectorAll<HTMLButtonElement>("[data-range]").forEach((button) => {
    button.classList.remove("active");
  });
}

function markRefreshSuccess() {
  elements.lastRefresh.textContent = formatTimestamp(new Date().toISOString());
}

function byId(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing element: ${id}`);
  return element;
}

function formatTimestamp(value: string | null): string {
  if (!value) return "-";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "medium"
  }).format(new Date(value));
}

function localDateTimeToIso(value: string): string | null {
  if (!value) return null;
  return new Date(value).toISOString();
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function windDirectionLabel(degrees: number): string {
  const normalized = ((degrees % 360) + 360) % 360;
  const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return directions[Math.round(normalized / 45) % directions.length]!;
}

function destinationPoint(
  latitude: number,
  longitude: number,
  bearingDegrees: number,
  distanceMeters: number
): [number, number] {
  const radiusMeters = 6371000;
  const bearing = bearingDegrees * Math.PI / 180;
  const lat1 = latitude * Math.PI / 180;
  const lon1 = longitude * Math.PI / 180;
  const angularDistance = distanceMeters / radiusMeters;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
    Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing)
  );
  const lon2 = lon1 + Math.atan2(
    Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
    Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
  );

  return [lat2 * 180 / Math.PI, lon2 * 180 / Math.PI];
}
